import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { FormRow, inputClass } from "../components/FormKit";
import { useAuth } from "../auth/AuthContext";
import { labelize } from "../lib/format";
import {
  openDispute, myDisputes, getDispute, postDisputeMessage, submitEvidence,
  DISPUTE_CONTEXTS, type Dispute, type DisputeMessage, type DisputeEvidence,
} from "../lib/disputes";
import { uploadToSignedUrl } from "../lib/verification";

export function Disputes() {
  const { account } = useAuth();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [ctx, setCtx] = useState<string>(DISPUTE_CONTEXTS[0]);
  const [ctxId, setCtxId] = useState("");
  const [claim, setClaim] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<DisputeMessage[]>([]);
  const [evidence, setEvidence] = useState<DisputeEvidence[]>([]);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState<string | null>(null);

  async function refresh() {
    setDisputes((await myDisputes()).disputes);
  }
  useEffect(() => { void refresh(); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      await openDispute({ context_type: ctx, context_id: ctxId || undefined, claim, detail: detail || undefined });
      setOpenForm(false); setClaim(""); setDetail(""); setCtxId("");
      await refresh();
    } catch {
      setNote("Could not open the dispute.");
    } finally {
      setBusy(false);
    }
  }

  async function select(id: string) {
    setSelected(id);
    const d = await getDispute(id);
    setMessages(d.messages);
    setEvidence(d.evidence);
  }

  async function uploadEvidence(file: File) {
    if (!selected) return;
    setBusy(true);
    setNote(null);
    try {
      const { upload_url } = await submitEvidence(selected, file.name);
      if (upload_url) await uploadToSignedUrl(upload_url, file);
      setEvidence((await getDispute(selected)).evidence);
    } catch {
      setNote("Could not upload evidence.");
    } finally {
      setBusy(false);
    }
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      await postDisputeMessage(selected, reply);
      setReply("");
      setMessages((await getDispute(selected)).messages);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Disputes"
        subtitle="Open and track disputes over a transaction. A mediator reviews and resolves each case."
        actions={<Button onClick={() => setOpenForm((v) => !v)}>{openForm ? "Cancel" : "Open a dispute"}</Button>}
      />

      {openForm && (
        <Panel title="Open a dispute">
          <form className="grid gap-3" onSubmit={submit}>
            <FormRow label="What is this about?">
              <select className={inputClass} value={ctx} onChange={(e) => setCtx(e.target.value)}>
                {DISPUTE_CONTEXTS.map((c) => <option key={c} value={c}>{labelize(c)}</option>)}
              </select>
            </FormRow>
            <FormRow label="Reference ID (optional)">
              <input className={inputClass} value={ctxId} onChange={(e) => setCtxId(e.target.value)} placeholder="e.g. the placement or load id" />
            </FormRow>
            <FormRow label="Your claim">
              <input className={inputClass} value={claim} onChange={(e) => setClaim(e.target.value)} required placeholder="Summarize the issue" />
            </FormRow>
            <FormRow label="Details">
              <textarea className={inputClass} rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} />
            </FormRow>
            <Button type="submit" disabled={busy || !claim.trim()}>{busy ? "Opening…" : "Submit dispute"}</Button>
          </form>
        </Panel>
      )}

      <Panel title="My disputes">
        {disputes.length === 0 ? (
          <p className="text-sm text-muted">No disputes. If a transaction goes wrong, open one above.</p>
        ) : (
          <ul className="grid gap-2">
            {disputes.map((d) => (
              <li key={d.id} className="grid gap-2 border-t border-line py-3 first:border-t-0 first:pt-0">
                <button type="button" className="flex items-center justify-between gap-3 text-left" onClick={() => select(d.id === selected ? "" : d.id)}>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold">{d.claim}</span>
                    <span className="font-mono text-[11px] text-muted">{labelize(d.context_type)}{d.context_id ? ` · ${d.context_id.slice(0, 8)}` : ""}</span>
                  </span>
                  <StatusLabel status={d.status} />
                </button>
                {selected === d.id && (
                  <div className="grid gap-2 rounded-lg border border-line bg-surface-2 p-3">
                    {messages.length === 0 ? (
                      <p className="font-mono text-xs text-muted">No messages yet.</p>
                    ) : messages.map((m) => (
                      <div key={m.id} className={`text-sm ${m.author_account_id === account?.id ? "text-ink" : "text-muted"}`}>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-muted">{m.is_staff ? "Mediator" : m.author_account_id === account?.id ? "You" : "Other party"}</span>
                        <p>{m.body}</p>
                      </div>
                    ))}
                    {evidence.length > 0 && (
                      <div className="grid gap-1 border-t border-line pt-2">
                        {evidence.map((e) => (
                          // download (not open) so untrusted evidence can't execute as HTML/SVG in a viewer's tab.
                          <a key={e.id} href={e.url ?? "#"} download target="_blank" rel="noreferrer noopener" className="font-mono text-xs text-accent-strong underline">
                            📎 {e.filename ?? "evidence"}
                          </a>
                        ))}
                      </div>
                    )}
                    {d.status !== "resolved" && d.status !== "dismissed" && (
                      <>
                        <form className="flex gap-2" onSubmit={send}>
                          <input className={inputClass} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a message…" />
                          <Button type="submit" disabled={busy || !reply.trim()}>Send</Button>
                        </form>
                        <label className="font-mono text-[11px] text-muted">
                          Attach evidence
                          <input type="file" className="block text-xs" onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadEvidence(f); }} />
                        </label>
                      </>
                    )}
                    {d.resolution && <p className="font-mono text-xs text-muted">Outcome: <span className="text-ink">{d.resolution}</span>{d.resolution_action && d.resolution_action !== "none" ? ` (${d.resolution_action})` : ""}</p>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {note && <p className="font-mono text-sm text-danger">{note}</p>}
    </div>
  );
}
